import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { Kbd } from '../../components/Kbd';
import { useModifierLabel } from '../../lib/keyboard';

interface ShortcutHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Die Uebersicht der Tastenkuerzel, geoeffnet mit "?".
 *
 * **Ein Kuerzel, das nirgends steht, ist keines.** Wer es nicht kennt,
 * benutzt es nicht; wer es einmal gehoert hat, vergisst es bis zum naechsten
 * Mal. Deshalb diese Liste -- und deshalb steht sie hinter einer Taste, die
 * man auch dann findet, wenn man den Namen der Liste nicht kennt.
 *
 * Die Buchstaben kommen aus den Sprachdateien und nicht aus dem Code: "a"
 * fuer Antwort ist eine deutsche Merkhilfe (``useTicketShortcuts``).
 */
export function ShortcutHelp({ open, onOpenChange }: ShortcutHelpProps) {
  const { t } = useTranslation();
  const modifier = useModifierLabel();

  const groups: { title: string; rows: { keys: string[]; label: string }[] }[] = [
    {
      title: t('shortcuts.sectionGlobal'),
      rows: [
        { keys: [modifier, 'K'], label: t('shortcuts.palette') },
        { keys: ['?'], label: t('shortcuts.help') },
        { keys: [t('shortcuts.escapeKey')], label: t('shortcuts.close') },
      ],
    },
    {
      title: t('shortcuts.sectionTicket'),
      rows: [
        { keys: [t('shortcuts.keyReply').toUpperCase()], label: t('shortcuts.reply') },
        { keys: [t('shortcuts.keyNote').toUpperCase()], label: t('shortcuts.note') },
        { keys: [t('shortcuts.keyTime').toUpperCase()], label: t('shortcuts.time') },
        { keys: [modifier, '↵'], label: t('shortcuts.send') },
      ],
    },
  ];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content" aria-describedby={undefined}>
          <Dialog.Title>{t('shortcuts.title')}</Dialog.Title>
          <p className="page-header__subtitle">{t('shortcuts.hint')}</p>

          {groups.map((group) => (
            <section key={group.title} style={{ marginTop: 'var(--space-4)' }}>
              <h3>{group.title}</h3>
              <dl className="shortcut-list">
                {group.rows.map((row) => (
                  <div className="shortcut-list__row" key={row.label}>
                    <dt>
                      {row.keys.map((key) => (
                        <Kbd key={key}>{key}</Kbd>
                      ))}
                    </dt>
                    <dd>{row.label}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
            <Dialog.Close asChild>
              <Button>{t('app.close')}</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
